import * as React from "react";

import { CustomerCard } from "@/react/features/customer/card";
import { formatUnixTimestamp } from "@/react/datetime";

import { OuterContainer } from "@/react/features/support-tools/common/styled";
import {
  Title,
  InnerContainer,
  ActionTitle,
  ContainerBody,
  StyledUsersIcon,
  Paragraph,
  ToggleContainer,
  ActionStrip,
  ActionButton,
  StyledPlayIcon,
  StyledRefreshIcon,
  LastRefreshSpan,
} from "./styled";
import {
  useLazyGetAccessGroupingDataQuery,
  useUpdateAccessGroupingDataMutation,
} from "./api";
import StatusIndicator from "./StatusIndicator";
import {
  ReduxQueryStatuses,
  popToastOnSuccess,
  useReduxQueryResponseHandlers,
} from "../common";

const AccessGroupingPage = () => {
  const [currentStatus, setCurrentStatus] = React.useState(null);
  const [lastRefreshed, setLastRefreshed] = React.useState(null);
  const [fetchAccessGroupingData, queryResult] =
    useLazyGetAccessGroupingDataQuery();
  const [updateAccessGrouping, mutationResult] =
    useUpdateAccessGroupingDataMutation();
  const { logAndNotifyFailure } = useReduxQueryResponseHandlers();
  const isLoading =
    queryResult?.status === ReduxQueryStatuses.PENDING ||
    mutationResult?.status === ReduxQueryStatuses.PENDING;

  const updateLastRefreshed = React.useCallback(() => {
    const currentTimestamp = Date.now();
    setLastRefreshed(currentTimestamp);
    console.debug(
      `[updateLastRefreshed] Refreshed set to ${currentTimestamp}.`
    );
  }, []);

  const syncToggleState = React.useCallback((result) => {
    const currentData = result?.currentData;
    if (!currentData) {
      return;
    }

    const groupingEnabled = currentData?.groupingEnabled;
    setCurrentStatus(!!groupingEnabled);
  }, []);

  const onOptInActionClicked = async () => {
    await updateAccessGrouping(!currentStatus);
    await fetchAccessGroupingData();
  };

  const onRefresh = async () => {
    await fetchAccessGroupingData();
  };

  React.useEffect(() => {
    // When the component is mounted, start the first fetch, for the data for
    // the currently displayed customer.
    fetchAccessGroupingData();
  }, []);

  React.useEffect(() => {
    logAndNotifyFailure(queryResult);
    syncToggleState(queryResult);
    updateLastRefreshed();
  }, [queryResult]);

  React.useEffect(() => {
    logAndNotifyFailure(mutationResult);
    popToastOnSuccess(mutationResult, {
      message: "Process to update access control grouping initiated.",
    });
  }, [mutationResult]);

  return (
    <OuterContainer>
      <Title>Access Grouping</Title>
      <CustomerCard />
      <InnerContainer>
        <ActionTitle>
          <StyledUsersIcon />
          Access Group Scoping Opt-In
        </ActionTitle>
        <ContainerBody>
          <Paragraph>
            <strong>MENTIONS</strong>
            <ul>
              <li>Dates are using the user's locale.</li>
              <li>
                Pressing the "Start ~ Process" button will start an asynchronous
                operation to change the status. Its progress is not being
                reported back to this tool.
              </li>
              <li>
                When refreshing to check the status, please keep in mind that
                the previous status might remain cached for a few minutes.
                Please be patient.
              </li>
            </ul>
          </Paragraph>
          <ToggleContainer>
            <StatusIndicator isLoading={isLoading} value={currentStatus} />
          </ToggleContainer>
        </ContainerBody>
        <ActionStrip>
          <ActionButton onClick={onOptInActionClicked}>
            <StyledPlayIcon invertColors />
            Start Opt-in / Opt-out Process
          </ActionButton>
          <ActionButton onClick={onRefresh}>
            <StyledRefreshIcon invertColors size={16} />
            Refresh
          </ActionButton>
          <LastRefreshSpan>
            <strong>Last Refresh: </strong>
            <code>{queryResult?.status}</code> (at{" "}
            {formatUnixTimestamp(lastRefreshed)})
          </LastRefreshSpan>
        </ActionStrip>
      </InnerContainer>
    </OuterContainer>
  );
};
export default AccessGroupingPage;
